import { useCallback, useEffect, useRef, useState } from "react";
import { useMe } from "@/lib/auth";

const STORAGE_NS = "eb:userpref";

function storageKey(userId: string, key: string) {
  return `${STORAGE_NS}:${userId}:${key}`;
}

function readPref<T>(userId: string, key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(storageKey(userId, key));
    if (raw == null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writePref<T>(userId: string, key: string, value: T) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      storageKey(userId, key),
      JSON.stringify(value),
    );
  } catch {
    // ignore quota / private-mode failures
  }
}

/**
 * Per-user preference storage backed by localStorage and keyed by the
 * authenticated Clerk user id. Survives tab reloads and is scoped to
 * the device — distinct from `useTabState` which only lives for one tab.
 *
 * Hydration / auth-resolution model:
 * - Initial render returns the supplied default (no localStorage read,
 *   so SSR is safe and there is no "anon flicker" from a temporary
 *   anonymous storage slot).
 * - Once `/api/me` resolves and we have a real user id, we hydrate
 *   from that user's slot in a single effect.
 * - Writes that arrive BEFORE the user id resolves are buffered in a
 *   ref and flushed once we know who the user is, so an early click
 *   on a toggle doesn't get lost or written to a shared anon slot.
 * - Writes after hydration go straight to the user's slot AND update
 *   in-memory state.
 */
export function usePreference<T>(
  key: string,
  defaultValue: T,
): [T, (next: T | ((prev: T) => T)) => void] {
  const { me } = useMe();
  const userId = me?.id;
  const [value, setValueState] = useState<T>(defaultValue);
  // Tracks whether we've already hydrated from localStorage for the current
  // user and preference key. The key can include tenant scope, so a tenant
  // switch must hydrate the new slot even when the Clerk user is unchanged.
  const hydratedFor = useRef<string | null>(null);
  // Buffers writes that happen before we know the user id so they
  // can be flushed exactly once when /api/me resolves.
  const pendingWrite = useRef<T | undefined>(undefined);

  useEffect(() => {
    if (!userId) return;
    const hydrationScope = `${userId}:${key}`;
    if (hydratedFor.current === hydrationScope) return;
    hydratedFor.current = hydrationScope;

    if (pendingWrite.current !== undefined) {
      // The user already mutated the value before we knew who they
      // were — persist that intent rather than overwriting it with
      // whatever's on disk.
      writePref(userId, key, pendingWrite.current);
      setValueState(pendingWrite.current);
      pendingWrite.current = undefined;
      return;
    }

    const stored = readPref<T>(userId, key, defaultValue);
    setValueState(stored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, key]);

  useEffect(() => {
    if (!userId || typeof window === "undefined") return;
    const expectedKey = storageKey(userId, key);
    const onStorage = (event: StorageEvent) => {
      if (event.key !== expectedKey || event.newValue == null) return;

      // A StorageEvent with no storageArea is still useful in test/browser
      // shims. When present, only accept events originating from localStorage.
      if (event.storageArea) {
        try {
          if (event.storageArea !== window.localStorage) return;
        } catch {
          // If storage is unavailable, ignore the event rather than allowing
          // it to affect the in-memory preference or any product draft.
          return;
        }
      }

      try {
        setValueState(JSON.parse(event.newValue) as T);
      } catch {
        // Ignore malformed cross-tab values and keep the current preference.
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [userId, key]);

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValueState((prev) => {
        const resolved =
          typeof next === "function" ? (next as (p: T) => T)(prev) : next;
        if (userId) {
          writePref(userId, key, resolved);
        } else {
          // Buffer until /api/me resolves. We deliberately do NOT
          // write to an anonymous slot here — multiple users on the
          // same device should never see each other's prefs.
          pendingWrite.current = resolved;
        }
        return resolved;
      });
    },
    [userId, key],
  );

  return [value, setValue];
}
